import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import URLS from 'URLS';
import { useTranslation } from 'react-i18next';
import { useIsSchoolAuth } from 'hooks/Auth';
import { useAdmin } from 'apps/AdminPanel/hooks/useAdmin';

// Material UI Components
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// Assets/Icons
import LOGO from 'assets/icons/ECPlayLogo.svg';
import MenuIcon from '@mui/icons-material/MenuRounded';
import AccountIcon from '@mui/icons-material/AccountCircleOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import ChallengeIcon from '@mui/icons-material/EmojiEventsRounded';
import PlayerIcon from '@mui/icons-material/PlayArrowRounded';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AcademyIcon from '@mui/icons-material/School';
import ResourcesIcon from '@mui/icons-material/LibraryBooks';

const Sidebar = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isSchoolAuth, _, schoolAuthDoc] = useIsSchoolAuth();
  const isSchoolStudent = schoolAuthDoc !== null ? schoolAuthDoc?.data().role === 'student' : false;
  const [admin, adminIsLoading] = useAdmin();

  const toggleDrawer = () => setOpen((prev) => !prev);

  type ListItemProps = {
    icon: ReactNode;
    text: string;
    to: string;
    external?: boolean;
  };

  const ListItem = ({ to, icon, text, external }: ListItemProps) => (
    <ListItemButton
      onClick={() => {
        if (external) {
          window.open(to);
        } else {
          navigate(to);
        }
      }}
      selected={location.pathname === to}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={text} />
    </ListItemButton>
  );

  return (
    <>
      <IconButton aria-label='Toggle sidebar' onClick={toggleDrawer}>
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor='left'
        onClose={toggleDrawer}
        open={open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 275,
              width: '70%',
              maxWidth: 400,
            },
          },
        }}>
        <List>
          <img alt='EC-Play logo' height='40em' src={LOGO} style={{ margin: '8px 16px', width: 150, height: 'auto' }} width='auto' />
          {import.meta.env.VITE_SHOW_EVENTS === 'TRUE' && isSchoolAuth !== true && (
            <ListItem icon={<EventIcon />} text={t('APPS.EVENTS')} to={URLS.EVENTS_EVENTS} />
          )}
          {import.meta.env.VITE_SHOW_CHALLENGE === 'TRUE' && isSchoolAuth !== true && (
            <ListItem icon={<ChallengeIcon />} text={t('APPS.CHALLENGE')} to={URLS.CHALLENGE} />
          )}
          {import.meta.env.VITE_SHOW_PLAYER === 'TRUE' && <ListItem icon={<PlayerIcon />} text={t('APPS.EDTECH')} to={URLS.EDTECH} />}
          {import.meta.env.VITE_SHOW_RESOURCES === 'TRUE' && <ListItem icon={<ResourcesIcon />} text={t('APPS.RESOURCES')} to={URLS.RESOURCES} />}
          {import.meta.env.VITE_SHOW_ACADEMY === 'TRUE' && <ListItem icon={<AcademyIcon />} text={t('APPS.ACADEMY')} to={URLS.ACADEMY} />}
          {isSchoolStudent !== true && <ListItem external icon={<ShoppingCartIcon />} text={t('APPS.SHOP')} to={URLS.SHOP} />}
          {/*
          This is temporary removed due to "about us" not existing
            <ListItem external icon={<InfoIcon />} text={t('APPS.ORG')} to={URLS.ORG} />
            */}
        </List>
        <Divider />
        <List>
          {isSchoolAuth !== true && <ListItem icon={<ChatIcon />} text={t('CHATS.HEADER')} to={URLS.CHAT} />}
          <ListItem icon={<AccountIcon />} text={t('APPS.ACCOUNT')} to={URLS.ACCOUNT} />
        </List>
      </Drawer>
    </>
  );
};

export default Sidebar;
